import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App.js";
import Dashboard from "../home-sections/Dashboard";
import Packages from "../home-sections/Packages";
import Bookings from "../home-sections/Bookings";
import Earnings from "../home-sections/Earnings";
import Reviews from "../home-sections/Reviews";
import Profile from "../home-sections/Profile";
import Help from "../home-sections/Help";
import BankAccountForm from "../home-sections/BankAccountForm";
import BookingDetailsModal from "../components/BookingDetailsModals";
import PackageDetailsModal from "../components/PackageDetailsModal";
import logohead from "../assets/logothin.png";
import "../styles/Home.css";
import "../home-styling/bookings.css";
import "../home-styling/earnings.css";

// Constants for better maintainability
const API_ENDPOINTS = {
  PACKAGES: '/api/packages',
  PACKAGE_DETAILS: '/api/packages',
  PROFILE: '/api/tour-operators/profile/complete',
  PROFILE_UPDATE: '/api/tour-operators/update-basic',
  PROFILE_BUSINESS: '/api/tour-operators/profile',
  BANK_ACCOUNT: '/api/tour-operators/bank-account',
  BOOKINGS: `https://uragate.in/websites/webapi2/tour-package/opr/operator-bookings`,
  FINANCE: 'https://uragate.in/websites/webapi2/tour-package/opr/operator-finance'
};

const LOCAL_STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  OPERATOR_DATA: 'operatorData'
};

const Home = () => {
  const [activeSection, setActiveSection] = useState("dashboard");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [dashboardPackages, setDashboardPackages] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bookingStats, setBookingStats] = useState({
    pending: 0,
    confirmed: 0,
    cancelled: 0,
    total: 0,
  });
  const [financeData, setFinanceData] = useState({
    totalEarnings: 0,
    pendingAmount: 0,
    paidAmount: 0,
    recentTransactions: [],
    earningsByMonth: [],
  });
  const [loading, setLoading] = useState({
    profile: false,
    packages: false,
    dashboard: false,
    bookings: false,
    earnings: false,
    bankAccount: false,
    reviews: false,
    help: false,
  });
  const [error, setError] = useState(null);
  const [bookingError, setBookingError] = useState(null);
  const [earningsError, setEarningsError] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileSaveLoading, setProfileSaveLoading] = useState(false);
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);
  const [bankAccountData, setBankAccountData] = useState(null);

  // Modal states
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);

  const navigate = useNavigate();
  const { logout: authLogout, getOperatorData } = useAuth();

  // Memoized utility functions
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN);
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const getOperatorId = useCallback(() => {
    const operatorData = getOperatorData();
    if (operatorData) {
      try {
        const operatorId = operatorData.operator_id || operatorData.id;
        return operatorId;
      } catch (error) {
        console.error("Error parsing operator data:", error);
        return null;
      }
    }
    return null;
  }, [getOperatorData]);

  const handleUnauthorized = useCallback(() => {
    console.log('🚫 Unauthorized, clearing auth data and redirecting to login');
    authLogout();
    window.location.href = '/login';
  }, [authLogout]);

  // Navigation functionality
  const handleNavClick = useCallback((section) => {
    console.log(`📱 Navigation click: ${section}`);
    setActiveSection(section);
    window.location.hash = section;
    setMobileNavOpen(false);
    document.body.style.overflow = "";
  }, []);

  // Mobile navigation functions
  const openMobileNav = useCallback(() => {
    setMobileNavOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeMobileNav = useCallback(() => {
    setMobileNavOpen(false);
    document.body.style.overflow = "";
  }, []);

  // Initialize home page
  const initializeHomePage = useCallback(() => {
    console.log('🏠 Initializing home page');
    const hash = window.location.hash.substring(1);
    const section = hash || "dashboard";
    console.log(`📍 Setting active section to: ${section}`);
    setActiveSection(section);
  }, []);

  // Data loading functions
  const loadBankAccount = useCallback(async () => {
    try {
      console.log("Loading bank account data...");
      setLoading((prev) => ({ ...prev, bankAccount: true }));

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.BANK_ACCOUNT}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        if (response.status === 404) {
          setBankAccountData(null);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setBankAccountData(result.data);
      } else {
        setBankAccountData(null);
      }
    } catch (error) {
      console.error("Error loading bank account:", error);
      setBankAccountData(null);
    } finally {
      setLoading((prev) => ({ ...prev, bankAccount: false }));
    }
  }, [getAuthHeaders, handleUnauthorized]);

  const loadProfileData = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, profile: true }));
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.PROFILE}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setProfileData(result.data);
        await loadBankAccount();
      } else {
        throw new Error("No profile data found in response");
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      setError("Failed to load profile data: " + error.message);
      setProfileData(null);
    } finally {
      setLoading((prev) => ({ ...prev, profile: false }));
    }
  }, [getAuthHeaders, handleUnauthorized, loadBankAccount]);

  const loadDashboardPackages = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, dashboard: true }));
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL }${API_ENDPOINTS.PACKAGES}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      let packagesData = [];
      if (result.success && result.data) {
        packagesData = result.data;
      } else if (Array.isArray(result)) {
        packagesData = result;
      } else if (result.packages) {
        packagesData = result.packages;
      }

      const dashboardPkgs = packagesData.slice(0, 3);
      setDashboardPackages(dashboardPkgs);
      setPackages(packagesData);
    } catch (error) {
      console.error("Error loading dashboard packages:", error);
      setError("Failed to load packages: " + error.message);
      setDashboardPackages([]);
    } finally {
      setLoading((prev) => ({ ...prev, dashboard: false }));
    }
  }, [getAuthHeaders, handleUnauthorized]);

  const loadPackages = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, packages: true }));
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.PACKAGES}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      let packagesData = [];
      if (result.success && result.data) {
        packagesData = result.data;
      } else if (Array.isArray(result)) {
        packagesData = result;
      } else if (result.packages) {
        packagesData = result.packages;
      }

      setPackages(packagesData);
    } catch (error) {
      console.error("Error loading packages:", error);
      setError("Failed to load packages: " + error.message);
      setPackages([]);
    } finally {
      setLoading((prev) => ({ ...prev, packages: false }));
    }
  }, [getAuthHeaders, handleUnauthorized]);

  const loadBookings = useCallback(async () => {
    const operator_id = getOperatorId();
    console.log('📊 Loading bookings for operator:', operator_id);
    
    try {
      setLoading((prev) => ({ ...prev, bookings: true }));
      setBookingError(null);

      const response = await fetch(
        `${API_ENDPOINTS.BOOKINGS}?operator_id=${operator_id}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        const bookingsData = result.data;
        setBookings(bookingsData);

        // Calculate booking statistics
        const stats = {
          pending: bookingsData.filter(
            (booking) =>
              booking.booking_status?.toLowerCase() === "pending" ||
              booking.status?.toLowerCase() === "pending"
          ).length,
          confirmed: bookingsData.filter(
            (booking) =>
              booking.booking_status?.toLowerCase() === "confirmed" ||
              booking.status?.toLowerCase() === "confirmed"
          ).length,
          cancelled: bookingsData.filter(
            (booking) =>
              booking.booking_status?.toLowerCase() === "cancelled" ||
              booking.status?.toLowerCase() === "cancelled"
          ).length,
          total: bookingsData.length,
        };

        setBookingStats(stats);
      } else {
        throw new Error("No bookings data found in response");
      }
    } catch (error) {
      console.error("Error loading bookings:", error);
      setBookingError("Failed to load bookings: " + error.message);
      setBookings([]);
    } finally {
      setLoading((prev) => ({ ...prev, bookings: false }));
    }
  }, [getAuthHeaders, getOperatorId, handleUnauthorized]);

  const loadFinanceData = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, earnings: true }));
      setEarningsError(null);

      const operatorId = getOperatorId();
      if (!operatorId) {
        throw new Error("Operator ID not found");
      }

      const response = await fetch(
        `${API_ENDPOINTS.FINANCE}?operator_id=${operatorId}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to load finance data: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        const financeData = result.data;

        const transactions =
          financeData.transactions || financeData.recentTransactions || [];
        const totalEarnings =
          financeData.totalEarnings ||
          transactions.reduce(
            (sum, transaction) => sum + (parseFloat(transaction.amount) || 0),
            0
          );

        const pendingAmount =
          financeData.pendingAmount ||
          transactions
            .filter((t) => t.status?.toLowerCase() === "pending")
            .reduce(
              (sum, transaction) => sum + (parseFloat(transaction.amount) || 0),
              0
            );

        const paidAmount =
          financeData.paidAmount ||
          transactions
            .filter(
              (t) =>
                t.status?.toLowerCase() === "paid" ||
                t.status?.toLowerCase() === "completed"
            )
            .reduce(
              (sum, transaction) => sum + (parseFloat(transaction.amount) || 0),
              0
            );

        setFinanceData({
          totalEarnings,
          pendingAmount,
          paidAmount,
          recentTransactions: transactions.slice(0, 10),
          earningsByMonth:
            financeData.earningsByMonth || financeData.monthlyEarnings || [],
        });
      } else {
        throw new Error("No finance data found in response");
      }
    } catch (error) {
      console.error("Error loading finance data:", error);
      setEarningsError("Failed to load earnings data: " + error.message);
      setFinanceData({
        totalEarnings: 0,
        pendingAmount: 0,
        paidAmount: 0,
        recentTransactions: [],
        earningsByMonth: [],
      });
    } finally {
      setLoading((prev) => ({ ...prev, earnings: false }));
    }
  }, [getAuthHeaders, getOperatorId, handleUnauthorized]);

  // Initialize home page when component mounts
  useEffect(() => {
    console.log('✅ Home: Initializing...');
    initializeHomePage();
  }, [initializeHomePage]);

  // Load data when section changes
  useEffect(() => {
    const loadSectionData = async () => {
      console.log(`🔄 Loading data for section: ${activeSection}`);
      
      switch (activeSection) {
        case "packages":
          await loadPackages();
          break;
        case "dashboard":
          await loadDashboardPackages();
          break;
        case "profile":
          await loadProfileData();
          break;
        case "bookings":
          await loadBookings();
          break;
        case "earnings":
          await loadFinanceData();
          break;
        default:
          break;
      }
    };

    loadSectionData();
  }, [
    activeSection,
    loadPackages,
    loadDashboardPackages,
    loadProfileData,
    loadBookings,
    loadFinanceData,
  ]);

  // Bank Account Functions
  const handleSaveBankAccount = useCallback(async (formData) => {
    try {
      setLoading((prev) => ({ ...prev, bankAccount: true }));

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.BANK_ACCOUNT}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN)}`,
          },
          body: formData,
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to save bank account");
      }

      const result = await response.json();

      if (result.success) {
        alert("Bank account saved successfully!");
        setShowBankAccountForm(false);
        await loadBankAccount();
        await loadProfileData();
      } else {
        throw new Error(result.message || "Failed to save bank account");
      }
    } catch (error) {
      console.error("Error saving bank account:", error);
      alert("Failed to save bank account: " + error.message);
    } finally {
      setLoading((prev) => ({ ...prev, bankAccount: false }));
    }
  }, [handleUnauthorized, loadBankAccount, loadProfileData]);

  const handleDeleteBankAccount = useCallback(async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete your bank account? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, bankAccount: true }));

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.BANK_ACCOUNT}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        alert("Bank account deleted successfully!");
        setBankAccountData(null);
        await loadProfileData();
      } else {
        throw new Error(result.message || "Failed to delete bank account");
      }
    } catch (error) {
      console.error("Error deleting bank account:", error);
      alert("Failed to delete bank account: " + error.message);
    } finally {
      setLoading((prev) => ({ ...prev, bankAccount: false }));
    }
  }, [getAuthHeaders, handleUnauthorized, loadProfileData]);

  const handleAddBankAccount = useCallback(() => {
    setBankAccountData(null);
    setShowBankAccountForm(true);
  }, []);

  const handleEditBankAccount = useCallback(() => {
    setBankAccountData(profileData?.bankAccount || null);
    setShowBankAccountForm(true);
  }, [profileData]);

  const handleCloseBankAccountForm = useCallback(() => {
    setShowBankAccountForm(false);
    setBankAccountData(null);
  }, []);

  // Modal handlers
  const handleCloseBookingModal = useCallback(() => {
    setShowBookingModal(false);
    setSelectedBooking(null);
  }, []);

  const handleClosePackageModal = useCallback(() => {
    setShowPackageModal(false);
    setSelectedPackage(null);
  }, []);

  // ✅ FIXED: Sign out functionality
  const handleSignOut = useCallback((event) => {
    event.preventDefault();
    console.log('🚪 Signing out...');
    
    // Clear all auth data
    localStorage.removeItem('authToken');
    localStorage.removeItem('operatorData');
    
    // Clear session storage too
    sessionStorage.clear();
    
    // Update auth state
    authLogout();
    
    // Force immediate redirect to login with reload
    window.location.href = '/login';
    window.location.reload();
  }, [authLogout]);

  // Navigation handlers
  const handleViewBookings = useCallback((e) => {
    e?.preventDefault();
    handleNavClick("bookings");
  }, [handleNavClick]);

  const handleViewEarnings = useCallback((e) => {
    e?.preventDefault();
    handleNavClick("earnings");
  }, [handleNavClick]);

  // Profile update functions
  const handleSaveProfile = useCallback(async (profileData) => {
    try {
      setProfileSaveLoading(true);
      setError(null);

      const basicResponse = await fetch(
        `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.PROFILE_UPDATE}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            business_number: profileData.business_number,
            business_name: profileData.business_name,
            phone: profileData.phone,
            email: profileData.email,
          }),
        }
      );

      if (!basicResponse.ok) {
        const errorData = await basicResponse.json();
        throw new Error(
          errorData.error || "Failed to update basic information"
        );
      }

      const profileResponse = await fetch(
        `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.PROFILE_BUSINESS}`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            operatorId: profileData.id,
            agencyName: profileData.agency_name,
            address: profileData.address,
            businessType: profileData.business_type,
            description: profileData.description,
            socialLinks: profileData.social_links,
            locationCoords: profileData.location_coords,
          }),
        }
      );

      if (!profileResponse.ok) {
        const errorData = await profileResponse.json();
        throw new Error(errorData.error || "Failed to update business profile");
      }

      const result = await profileResponse.json();

      if (result.success) {
        alert("Profile updated successfully!");
        setIsEditingProfile(false);
        await loadProfileData();
      } else {
        throw new Error(result.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setError("Failed to update profile: " + error.message);
    } finally {
      setProfileSaveLoading(false);
    }
  }, [getAuthHeaders, loadProfileData]);

  // Utility functions
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, []);

  const formatTransactionDate = useCallback((dateString) => {
    if (!dateString) return "Not specified";

    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  }, []);

  const getTransactionStatusClass = useCallback((status) => {
    if (!status) return "status-unknown";

    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "paid":
      case "completed":
      case "success":
        return "status-paid";
      case "pending":
      case "processing":
        return "status-pending";
      case "failed":
      case "cancelled":
      case "refunded":
        return "status-cancelled";
      default:
        return "status-unknown";
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Not specified";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  }, []);

  const getStatusBadgeClass = useCallback((status) => {
    if (!status) return "status-unknown";

    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "confirmed":
      case "completed":
        return "status-confirmed";
      case "pending":
      case "processing":
        return "status-pending";
      case "cancelled":
      case "rejected":
        return "status-cancelled";
      default:
        return "status-unknown";
    }
  }, []);

  // Booking functions
  const handleBookingAction = useCallback(async (booking, action) => {
    if (!window.confirm(`Are you sure you want to ${action} this booking?`)) {
      return;
    }

    try {
      const response = await fetch(
        `https://uragate.in/websites/webapi2/tour-package/opr/booking/${booking.booking_id}/${action}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to ${action} booking`);
      }

      const result = await response.json();

      if (result.success) {
        alert(`Booking ${action}ed successfully!`);
        loadBookings();
      } else {
        throw new Error(result.message || `Failed to ${action} booking`);
      }
    } catch (error) {
      console.error(`Error ${action}ing booking:`, error);
      alert(`Failed to ${action} booking. Please try again.`);
    }
  }, [getAuthHeaders, handleUnauthorized, loadBookings]);

  const viewBookingDetails = useCallback((booking) => {
    setSelectedBooking(booking);
    setShowBookingModal(true);
  }, []);

  const viewPackageDetails = useCallback(async (packageId) => {
    try {
      if (!packageId || packageId === "undefined" || packageId === "null") {
        console.error("Invalid package ID:", packageId);
        alert("Invalid package ID. Please try again.");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.PACKAGE_DETAILS}/${packageId}/details`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (response.status === 404) {
        alert("Package not found. It may have been deleted or you don't have access to it.");
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to fetch package details");
      }

      const packageData = result.data;

      let packageInfo, days, summary;

      if (packageData.package) {
        packageInfo = packageData.package;
        days = packageData.days || [];
        summary = packageData.summary || {
          totalDays: days.length,
          totalNights: days.filter((day) => !day.no_accommodation).length,
        };
      } else if (packageData.days) {
        packageInfo = { ...packageData };
        days = packageData.days;
        delete packageInfo.days;
        summary = packageData.summary || {
          totalDays: days.length,
          totalNights: days.filter((day) => !day.no_accommodation).length,
        };
      } else {
        packageInfo = packageData;
        days = [];
        summary = {
          totalDays: 0,
          totalNights: 0,
        };
      }

      setSelectedPackage({ packageInfo, days, summary });
      setShowPackageModal(true);
    } catch (error) {
      console.error("Error loading package details:", error);
      alert("Error loading package details. Please try again.");
    }
  }, [getAuthHeaders, handleUnauthorized]);

  const deletePackage = useCallback(async (packageId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this package? This action cannot be undone."
      )
    ) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.PACKAGES}/${packageId}`,
          {
            method: "DELETE",
            headers: getAuthHeaders(),
          }
        );

        if (response.status === 401) {
          handleUnauthorized();
          return;
        }

        const result = await response.json();

        if (result.success) {
          alert("Package deleted successfully!");
          loadPackages();
          loadDashboardPackages();
        } else {
          throw new Error(result.message || "Failed to delete package");
        }
      } catch (error) {
        console.error("Error deleting package:", error);
        alert("Error deleting package. Please try again.");
      }
    }
  }, [getAuthHeaders, handleUnauthorized, loadPackages, loadDashboardPackages]);

  // Navigation handlers
  const handleCreatePackage = useCallback(() => {
    navigate("/create-package");
  }, [navigate]);

  const handleViewAll = useCallback((e) => {
    e.preventDefault();
    handleNavClick("packages");
  }, [handleNavClick]);

  // Render navigation links
  const renderNavLink = useCallback((href, icon, text) => (
    <a
      href={`#${href}`}
      className={`nav-link ${activeSection === href ? "active" : ""}`}
      onClick={(e) => {
        e.preventDefault();
        handleNavClick(href);
      }}
      aria-label={text}
    >
      <i className={icon} aria-hidden="true"></i>
      <span>{text}</span>
    </a>
  ), [activeSection, handleNavClick]);

  return (
    <div className="home-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="hamburger-menu" onClick={openMobileNav} aria-label="Open menu">
            <div className="hamburger-stripes">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>

          <div className="business-info">
            <div className="business-icon">
              <img src={logohead} alt="URACAB Logo" loading="lazy" />
            </div>
            <div className="business-text">
              <span className="business-title">Operator</span>
              <span className="business-title">Panel</span>
            </div>
          </div>

          <div className="signout">
            <div className="signout-btn">
              <button onClick={handleSignOut} className="signout-link">
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Navigation */}
      <nav className="dashboard-nav" aria-label="Main navigation">
        {renderNavLink("dashboard", "fas fa-tachometer-alt", "Dashboard")}
        {renderNavLink("packages", "fas fa-suitcase", "Packages")}
        {renderNavLink("bookings", "fas fa-calendar-check", "Bookings")}
        {renderNavLink("earnings", "fas fa-chart-line", "Earnings")}
        {renderNavLink("reviews", "fas fa-star", "Reviews")}
        {renderNavLink("help", "fas fa-question-circle", "Help")}
        {renderNavLink("profile", "fas fa-user", "Profile")}
      </nav>

      {/* Mobile Navigation Overlay */}
      {mobileNavOpen && (
        <div
          className="mobile-nav-overlay active"
          onClick={closeMobileNav}
          aria-label="Close menu"
        ></div>
      )}

      {/* Mobile Sidebar Navigation */}
      <nav
        className={`dashboard-nav mobile-nav ${mobileNavOpen ? "active" : ""}`}
        aria-label="Mobile navigation"
      >
        <div className="nav-close" onClick={closeMobileNav} aria-label="Close menu">
          <p>X</p>
        </div>
        {renderNavLink("dashboard", "fas fa-tachometer-alt", "Dashboard")}
        {renderNavLink("packages", "fas fa-suitcase", "Packages")}
        {renderNavLink("bookings", "fas fa-calendar-check", "Bookings")}
        {renderNavLink("earnings", "fas fa-chart-line", "Earnings")}
        {renderNavLink("reviews", "fas fa-star", "Reviews")}
        {renderNavLink("help", "fas fa-question-circle", "Help")}
        {renderNavLink("profile", "fas fa-user", "Profile")}

        {/* Sign Out in Mobile Navigation */}
        <div className="mobile-signout">
          <a
            href="#signout"
            className="nav-link"
            onClick={(e) => {
              e.preventDefault();
              handleSignOut(e);
              closeMobileNav();
            }}
          >
            <i className="fas fa-sign-out-alt"></i>
            <span>Sign Out</span>
          </a>
        </div>
      </nav>

      {/* Bank Account Form Modal */}
      {showBankAccountForm && (
        <BankAccountForm
          bankAccount={bankAccountData}
          onSave={handleSaveBankAccount}
          onCancel={handleCloseBankAccountForm}
          loading={loading.bankAccount}
        />
      )}

      {/* Booking Details Modal */}
      {showBookingModal && (
        <BookingDetailsModal
          booking={selectedBooking}
          onClose={handleCloseBookingModal}
          formatDate={formatDate}
          getStatusBadgeClass={getStatusBadgeClass}
        />
      )}

      {/* Package Details Modal */}
      {showPackageModal && (
        <PackageDetailsModal
          packageData={selectedPackage}
          onClose={handleClosePackageModal}
        />
      )}

      {/* Content Sections */}
      <div
        id="dashboard-content"
        className={`content-section ${activeSection === "dashboard" ? "active" : ""}`}
        aria-labelledby="dashboard-heading"
      >
        <Dashboard
          dashboardPackages={dashboardPackages}
          packages={packages}
          bookingStats={bookingStats}
          financeData={financeData}
          loading={loading}
          handleCreatePackage={handleCreatePackage}
          handleViewAll={handleViewAll}
          viewPackageDetails={viewPackageDetails}
          formatCurrency={formatCurrency}
          handleViewBookings={handleViewBookings}
          handleViewEarnings={handleViewEarnings}
          isVerified={
            profileData?.is_verified === 1 || profileData?.is_verified === true
          }
        />
      </div>

      <div
        id="packages-content"
        className={`content-section ${activeSection === "packages" ? "active" : ""}`}
        aria-labelledby="packages-heading"
      >
        <Packages
          packages={packages}
          loading={loading}
          viewPackageDetails={viewPackageDetails}
          deletePackage={deletePackage}
        />
      </div>

      <div
        id="bookings-content"
        className={`content-section ${activeSection === "bookings" ? "active" : ""}`}
        aria-labelledby="bookings-heading"
      >
        <Bookings
          bookings={bookings}
          bookingStats={bookingStats}
          loading={loading}
          bookingError={bookingError}
          loadBookings={loadBookings}
          handleBookingAction={handleBookingAction}
          viewBookingDetails={viewBookingDetails}
          formatDate={formatDate}
          getStatusBadgeClass={getStatusBadgeClass}
          getAuthHeaders={getAuthHeaders}
          getOperatorId={getOperatorId}
        />
      </div>

      <div
        id="earnings-content"
        className={`content-section ${activeSection === "earnings" ? "active" : ""}`}
        aria-labelledby="earnings-heading"
      >
        <Earnings
          financeData={financeData}
          loading={loading}
          earningsError={earningsError}
          formatCurrency={formatCurrency}
          formatTransactionDate={formatTransactionDate}
          getTransactionStatusClass={getTransactionStatusClass}
        />
      </div>

      {/* Reviews Section */}
      <div
        id="reviews-content"
        className={`content-section ${activeSection === "reviews" ? "active" : ""}`}
        aria-labelledby="reviews-heading"
      >
        <Reviews
          loading={loading}
          getAuthHeaders={getAuthHeaders}
          handleUnauthorized={handleUnauthorized}
          formatDate={formatDate}
          getOperatorId={getOperatorId}
        />
      </div>

      {/* Help Section */}
      <div
        id="help-content"
        className={`content-section ${activeSection === "help" ? "active" : ""}`}
        aria-labelledby="help-heading"
      >
        <Help loading={loading} />
      </div>

      <div
        id="profile-content"
        className={`content-section ${activeSection === "profile" ? "active" : ""}`}
        aria-labelledby="profile-heading"
      >
        <Profile
          profileData={profileData}
          packages={packages}
          loading={loading}
          error={error}
          isEditingProfile={isEditingProfile}
          profileSaveLoading={profileSaveLoading}
          setIsEditingProfile={setIsEditingProfile}
          handleSaveProfile={handleSaveProfile}
          loadProfileData={loadProfileData}
          formatCurrency={formatCurrency}
          onAddBankAccount={handleAddBankAccount}
          onEditBankAccount={handleEditBankAccount}
          onDeleteBankAccount={handleDeleteBankAccount}
          bankAccountLoading={loading.bankAccount}
        />
      </div>
    </div>
  );
};

export default Home;